import { useApiQuery } from '../useApiQuery';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewsV2Endpoints } from '@/lib/api/endpoints';
import { apiGet, operationsClient } from '@/lib/api';
import type { ReviewsStats } from '@/types/review';

/**
 * Aggregates for the top strip (spec §12.2), read from the server.
 *
 * This used to compute the strip in the browser from a single `limit: 100`
 * list pull, which made two of the four numbers wrong in ways nothing on
 * screen disclosed:
 *
 *   - `total` was the length of that page, so a tenant with 300 reviews was
 *     told 100, and the average was taken over an arbitrary hundred of them.
 *   - `responseRate` counted `AI_DRAFTED` as replied, so it could read 100%
 *     with nothing actually posted where a customer could see it.
 *   - `medianTimeToReplyHours` was hardcoded null, because the list did not
 *     carry `replyPostedAt`.
 *
 * All three are now database aggregates over the whole window.
 */
export function useReviewStats(
  params: { windowDays?: number | 'all'; end?: string; start?: string } = {},
) {
  const windowDays = params.windowDays ?? 90;
  const { end, start } = params;

  return useApiQuery<ReviewsStats>({
    // `end` belongs in the key as well as the params: two custom ranges of the
    // same length ending on different days are different questions, and
    // keying on the length alone served one's answer for the other. `start` is
    // in the key for the same reason and one more: it overrides `windowDays`
    // server-side, so two ranges that clamp to the same day count are still
    // different questions.
    queryKey: reviewKeys.stats({ windowDays, end, start }),
    queryFn: async ({ signal }) => {
      const raw = await apiGet<{ data: ReviewsStats }>(
        operationsClient,
        reviewsV2Endpoints.stats(),
        {
          params: {
            windowDays,
            ...(end ? { end } : {}),
            ...(start ? { start } : {}),
          },
          signal,
        },
      );
      return raw.data;
    },
    refetchInterval: 30_000,
    refetchOnWindowFocus: false,
    showErrorToast: false,
  });
}
